/**
 * SSE Record/Playback Debug Panel (AIE-295)
 *
 * Floating panel (admin-only) for capturing and replaying SSE event streams.
 * Gated by localStorage flag set in Admin Settings.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Radio,
  Circle,
  Square,
  Play,
  Pause,
  Upload,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/cn";
import {
  SSERecorderPlayback,
  type SSERecording,
  type RecorderState,
  type PlaybackSpeed,
} from "@/utils/sseRecorderPlayback";
import { SSEConnectionManager, type SSEEvent } from "@/hooks/useModernSSE";
import { useIsAdmin } from "@/components/settings/sections/AdminSection";

const STORAGE_KEY = "sseDebugPanelEnabled";
const PANEL_OPEN_KEY = "sseDebugPanelOpen";

const SPEED_OPTIONS: PlaybackSpeed[] = [1, 2, 4, 8, "max"];

interface EventLogEntry {
  id: string;
  time: string;
  type: string;
  studyId: string;
  detail: string;
}

function formatEventForLog(event: SSEEvent): EventLogEntry {
  const time = new Date().toLocaleTimeString("en-US", { hour12: false });
  const studyId = event.study_id?.slice(0, 8) ?? "—";

  let detail = "";
  if (event.event === "trial.completed") {
    const payload = (event.data?.payload ?? event.data) as Record<
      string,
      unknown
    >;
    const trial = payload?.current_trial ?? "?";
    const best = payload?.best_value;
    detail = `trial ${String(trial)}`;
    if (best != null) detail += ` best=${Number(best).toFixed(4)}`;
  } else if (event.event === "study.status") {
    const payload = (event.data?.payload ?? event.data) as Record<
      string,
      unknown
    >;
    detail = String(payload?.status ?? "");
  }

  return { id: event.id, time, type: event.event, studyId, detail };
}

export function SSEDebugPanel() {
  // Gate: only render if the admin toggle is enabled
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setEnabled(localStorage.getItem(STORAGE_KEY) === "true");

    // Listen for storage changes (e.g., admin toggles in another tab or Settings)
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setEnabled(e.newValue === "true");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  if (!enabled) return null;

  return <SSEDebugPanelInner />;
}

// ---------------------------------------------------------------------------
// Inner panel (only mounts when enabled)
// ---------------------------------------------------------------------------

function SSEDebugPanelInner() {
  const isAdmin = useIsAdmin();
  const [isOpen, setIsOpen] = useState(
    () => localStorage.getItem(PANEL_OPEN_KEY) === "true",
  );
  const [recorderState, setRecorderState] = useState<RecorderState>("idle");
  const [isLeader, setIsLeader] = useState(
    () => SSEConnectionManager.getInstance().getIsLeaderTab(),
  );
  const [eventCount, setEventCount] = useState(0);
  const [playbackProgress, setPlaybackProgress] = useState({
    current: 0,
    total: 0,
  });
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(1);
  const [currentRecording, setCurrentRecording] =
    useState<SSERecording | null>(null);
  const [eventLog, setEventLog] = useState<EventLogEntry[]>([]);
  const [panelError, setPanelError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const recorderRef = useRef<SSERecorderPlayback | null>(null);

  // Poll leader-tab status (changes on BroadcastChannel leader election)
  useEffect(() => {
    const id = window.setInterval(() => {
      setIsLeader(SSEConnectionManager.getInstance().getIsLeaderTab());
    }, 2000);
    return () => window.clearInterval(id);
  }, []);

  // Create recorder instance
  useEffect(() => {
    const recorder = new SSERecorderPlayback({
      onStateChange: setRecorderState,
      onPlaybackProgress: (current, total) =>
        setPlaybackProgress({ current, total }),
      onEventRecorded: (event, count) => {
        setEventCount(count);
        // Throttle log updates during recording to reduce GC pressure
        // from intermediate array allocations on long-running studies
        if (count <= 1 || count % 5 === 0) {
          setEventLog((prev) => [formatEventForLog(event), ...prev].slice(0, 200));
        }
      },
      onEventPlayed: (event) => {
        setEventLog((prev) => [formatEventForLog(event), ...prev].slice(0, 200));
      },
    });
    recorderRef.current = recorder;
    return () => recorder.destroy();
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      localStorage.setItem(PANEL_OPEN_KEY, String(next));
      return next;
    });
  }, []);

  // Secondary admin gate — prevents manual localStorage manipulation from
  // exposing the panel to non-admin sessions on the same browser profile.
  if (!isAdmin) return null;

  const recorder = recorderRef.current;

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const handleRecord = () => recorder?.startRecording();

  const handleStopRecording = () => {
    const recording = recorder?.stopRecording();
    if (recording) setCurrentRecording(recording);
  };

  const handleExport = () => {
    if (currentRecording) {
      SSERecorderPlayback.exportToFile(currentRecording);
    }
  };

  const handleImport = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const recording = await SSERecorderPlayback.importFromFile(file);
      setCurrentRecording(recording);
      setEventCount(recording.events.length);
      setEventLog([]);
      setPanelError(null);
    } catch (err) {
      setPanelError(`Import failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    // Reset so the same file can be re-imported
    e.target.value = "";
  };

  const handlePlay = () => {
    if (currentRecording) {
      const started = recorder?.startPlayback(currentRecording, playbackSpeed);
      if (started === false) {
        setPanelError("Playback is only available on the leader tab.");
        return;
      }
      setPanelError(null);
      setEventLog([]);
    }
  };

  const handlePause = () => recorder?.pausePlayback();
  const handleResume = () => recorder?.resumePlayback(playbackSpeed);
  const handleStopPlayback = () => recorder?.stopPlayback();

  const handleSpeedChange = (speed: PlaybackSpeed) => {
    setPlaybackSpeed(speed);
    if (recorderState === "playing") {
      recorder?.changePlaybackSpeed(speed);
    }
  };

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------

  const isRecording = recorderState === "recording";
  const isPlaying = recorderState === "playing";
  const isPaused = recorderState === "paused";
  const isIdle = recorderState === "idle";
  const hasRecording = currentRecording !== null;
  const progressPct =
    playbackProgress.total > 0
      ? (playbackProgress.current / playbackProgress.total) * 100
      : 0;

  const durationStr = currentRecording
    ? `${(currentRecording.metadata.total_duration_ms / 1000).toFixed(1)}s`
    : "0s";

  return (
    <>
      {/* Toggle Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={togglePanel}
        className={cn(
          "fixed bottom-28 right-4 z-50 p-3 rounded-full shadow-lg",
          "bg-dark-800 border border-dark-700 hover:bg-dark-700",
          "transition-colors duration-200",
          isOpen && "bg-brand-blue hover:bg-brand-blue-dark",
          isRecording && "bg-red-600 hover:bg-red-700 animate-pulse",
        )}
        title="Toggle SSE Debug Panel"
      >
        <Radio className="h-5 w-5" />
      </motion.button>

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        className="hidden"
        onChange={handleFileChange}
      />

      {/* Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-44 right-4 z-40 w-[420px] bg-dark-900 border border-dark-700 rounded-lg shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: "500px" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-dark-700 bg-dark-800">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Radio className="h-4 w-4 text-green-400" />
                SSE Record/Playback
              </h3>
              <button
                onClick={togglePanel}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Status bar */}
            <div className="flex items-center gap-3 px-3 py-2 border-b border-dark-700 text-xs text-muted-foreground">
              <span>
                State:{" "}
                <span
                  className={cn(
                    "font-medium",
                    isRecording && "text-red-400",
                    isPlaying && "text-green-400",
                    isPaused && "text-yellow-400",
                    isIdle && "text-muted-foreground",
                  )}
                >
                  {recorderState.toUpperCase()}
                </span>
              </span>
              <span className="text-dark-600">|</span>
              <span>Events: {eventCount}</span>
              <span className="text-dark-600">|</span>
              <span>Duration: {durationStr}</span>
              <span className="text-dark-600">|</span>
              <span className={isLeader ? "text-green-400" : "text-dark-500"}>
                {isLeader ? "Leader" : "Follower"}
              </span>
            </div>

            {/* Recording controls */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-dark-700">
              <Button
                size="sm"
                variant="outline"
                onClick={handleRecord}
                disabled={!isIdle}
                className={cn(
                  "h-7 text-xs",
                  isIdle && "hover:border-red-500 hover:text-red-400",
                )}
              >
                <Circle
                  className={cn(
                    "h-3 w-3 mr-1",
                    isRecording && "text-red-500 fill-red-500",
                  )}
                />
                Record
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleStopRecording}
                disabled={!isRecording}
                className="h-7 text-xs"
              >
                <Square className="h-3 w-3 mr-1" />
                Stop
              </Button>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="outline"
                onClick={handleImport}
                disabled={isRecording || isPlaying}
                className="h-7 text-xs"
              >
                <Upload className="h-3 w-3 mr-1" />
                Import
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExport}
                disabled={!hasRecording || isRecording}
                className="h-7 text-xs"
              >
                <Download className="h-3 w-3 mr-1" />
                Export
              </Button>
            </div>

            {/* Speed + Playback controls */}
            <div className="px-3 py-2 border-b border-dark-700 space-y-2">
              {/* Speed */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground w-12">
                  Speed:
                </span>
                {SPEED_OPTIONS.map((speed) => (
                  <button
                    key={String(speed)}
                    onClick={() => handleSpeedChange(speed)}
                    className={cn(
                      "px-2 py-0.5 text-xs rounded border",
                      playbackSpeed === speed
                        ? "bg-brand-blue border-brand-blue text-white"
                        : "border-dark-600 text-muted-foreground hover:border-dark-500",
                    )}
                  >
                    {speed === "max" ? "Max" : `${speed}x`}
                  </button>
                ))}
              </div>

              {/* Playback buttons */}
              <div className="flex items-center gap-2">
                {!isPlaying && !isPaused && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePlay}
                    disabled={!hasRecording || isRecording || !isLeader}
                    title={!isLeader ? "Playback only available on leader tab" : undefined}
                    className="h-7 text-xs hover:border-green-500 hover:text-green-400"
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Play
                  </Button>
                )}
                {isPlaying && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePause}
                    className="h-7 text-xs"
                  >
                    <Pause className="h-3 w-3 mr-1" />
                    Pause
                  </Button>
                )}
                {isPaused && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleResume}
                    className="h-7 text-xs hover:border-green-500 hover:text-green-400"
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Resume
                  </Button>
                )}
                {(isPlaying || isPaused) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleStopPlayback}
                    className="h-7 text-xs"
                  >
                    <Square className="h-3 w-3 mr-1" />
                    Stop
                  </Button>
                )}
              </div>
            </div>

            {/* Progress bar */}
            {(isPlaying || isPaused) && (
              <div className="px-3 py-2 border-b border-dark-700">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-dark-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-blue rounded-full transition-all duration-200"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {playbackProgress.current}/{playbackProgress.total}
                  </span>
                </div>
              </div>
            )}

            {/* Inline error banner */}
            {panelError && (
              <div className="flex items-center justify-between px-3 py-2 bg-red-900/20 border-b border-red-700 text-red-400 text-xs">
                <span>{panelError}</span>
                <button
                  onClick={() => setPanelError(null)}
                  className="ml-2 hover:text-red-300"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {/* Event log */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {eventLog.length === 0 ? (
                <div className="flex items-center justify-center h-24 text-xs text-muted-foreground">
                  {isIdle && !hasRecording
                    ? "Press Record to capture SSE events"
                    : isIdle && hasRecording
                      ? "Recording loaded. Press Play to replay."
                      : "Waiting for events..."}
                </div>
              ) : (
                <div className="divide-y divide-dark-800">
                  {eventLog.map((entry, i) => (
                    <div
                      key={`${entry.id}-${i}`}
                      className="px-3 py-1.5 text-xs font-mono hover:bg-dark-800/50"
                    >
                      <span className="text-dark-400">{entry.time}</span>{" "}
                      <span
                        className={cn(
                          "font-medium",
                          entry.type === "trial.completed" && "text-blue-400",
                          entry.type === "study.status" && "text-green-400",
                          entry.type === "study.updated" && "text-yellow-400",
                        )}
                      >
                        {entry.type}
                      </span>{" "}
                      <span className="text-dark-400">{entry.studyId}</span>{" "}
                      <span className="text-muted-foreground">
                        {entry.detail}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
